/**
 * User detail drawer: uses shared DetailDrawer + DetailDrawerContent, data from API.
 * Shows user info, rent history, Give Promo, comment.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';
import Paper from '@mui/material/Paper';
import Chip from '@mui/material/Chip';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import Tooltip from '@mui/material/Tooltip';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import CardGiftcardOutlinedIcon from '@mui/icons-material/CardGiftcardOutlined';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import OpenInNewOutlinedIcon from '@mui/icons-material/OpenInNewOutlined';
import BlockIcon from '@mui/icons-material/Block';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import * as api from '../../modules/api';
import dayjs from 'dayjs';
import { getRentCurrencySymbol } from '../utils/rentDisplay';
import useNotifications from '../mui-crud/hooks/useNotifications/useNotifications';
import DetailDrawer from '../components/DetailDrawer';
import { Section, Field, CopyableField } from '../components/DetailDrawerContent';
import EntityCommentsSection, { normalizeEntityComments } from '../components/EntityCommentsSection';

/**
 * API may return date as "2025,4,25,19,15,28,779000000" or array.
 */
function formatDate(value) {
  if (value == null || value === '') return '—';
  if (Array.isArray(value)) {
    const [y, m, d, h = 0, min = 0, s = 0, nano = 0] = value;
    const ms = typeof nano === 'number' ? Math.round(nano / 1e6) : 0;
    const date = new Date(y, (m || 1) - 1, d || 1, h, min, s, ms);
    return dayjs(date).format('DD.MM.YYYY HH:mm');
  }
  if (typeof value === 'string' && /^\d{4},\d+,\d+/.test(value)) {
    const parts = value.split(',').map(Number);
    const [y, m, d, h = 0, min = 0, s = 0, nano = 0] = parts;
    const ms = nano ? Math.round(nano / 1e6) : 0;
    const date = new Date(y, (m || 1) - 1, d || 1, h, min, s, ms);
    return dayjs(date).format('DD.MM.YYYY HH:mm');
  }
  const d = dayjs(value);
  return d.isValid() ? d.format('DD.MM.YYYY HH:mm') : '—';
}

const CROSS_LINK_RENT_ID = 'crossLinkRentId';

export default function UserDetailDrawer({ user, open, onClose, onRefresh }) {
  const { t } = useTranslation();
  const history = useHistory();
  const notifications = useNotifications();
  const [blocking, setBlocking] = useState(false);
  const [blockDialogOpen, setBlockDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [blockActionLoading, setBlockActionLoading] = useState(false);
  const [deleteActionLoading, setDeleteActionLoading] = useState(false);
  const [rents, setRents] = useState([]);
  const [rentsLoading, setRentsLoading] = useState(false);
  const [rentsError, setRentsError] = useState(null);
  const [comments, setComments] = useState([]);
  const [hasPromo, setHasPromo] = useState(false);
  const [promoTariffs, setPromoTariffs] = useState([]);
  const [promoDialogOpen, setPromoDialogOpen] = useState(false);
  const [chosenTariffId, setChosenTariffId] = useState('');
  const [promoActionLoading, setPromoActionLoading] = useState(false);
  const [cardsDialogOpen, setCardsDialogOpen] = useState(false);
  const [cards, setCards] = useState([]);
  const [cardsLoading, setCardsLoading] = useState(false);
  const [cardsError, setCardsError] = useState(null);
  const [detailTab, setDetailTab] = useState(0);

  useEffect(() => {
    if (!open || !user?.phoneNumber) return;
    setRentsError(null);
    setRentsLoading(true);
    api
      .getUserRent(user.phoneNumber)
      .then((res) => {
        const data = res?.data;
        setRents(Array.isArray(data) ? data : data?.data ? (Array.isArray(data.data) ? data.data : []) : []);
      })
      .catch((e) => setRentsError(e?.message || t('drawer.user.failedToLoadHistory')))
      .finally(() => setRentsLoading(false));
  }, [open, user?.phoneNumber]);

  useEffect(() => {
    if (!open || !user?.id) return;
    setBlocking(Boolean(user.blocking));
    setDetailTab(0);
    setComments(normalizeEntityComments(user));
    api.GivePromoApi(user.id)
      .then((res) => setHasPromo(res?.data === true || res?.data === 'true'))
      .catch(() => setHasPromo(false));
    api
      .FindUsers(user.id)
      .then((res) => {
        const data = res?.data;
        if (!data) return;
        const entity = Array.isArray(data) ? data[0] : (data.user || data.data || data);
        if (entity) setComments(normalizeEntityComments(entity));
      })
      .catch(() => {});
  }, [open, user?.id, user?.comment, user?.comments, user?.blocking]);

  const handleOpenPromoDialog = useCallback(() => {
    setChosenTariffId('');
    setPromoDialogOpen(true);
    api.GetAllPromoTariffs()
      .then((res) => setPromoTariffs(Array.isArray(res?.data) ? res.data : []))
      .catch(() => setPromoTariffs([]));
  }, []);

  const handleGivePromo = useCallback(async () => {
    if (!user?.id) return;
    if (!hasPromo && !chosenTariffId) {
      notifications.show(t('drawer.user.choosePromoPlan'), { severity: 'warning' });
      return;
    }
    setPromoActionLoading(true);
    try {
      if (hasPromo) {
        await api.GivePromoToUserApi([user.id, false]);
        notifications.show(t('drawer.user.promoStopped'), { severity: 'success', autoHideDuration: 3000 });
        setHasPromo(false);
      } else {
        await api.GivePromoToUserApi([user.id, true, chosenTariffId]);
        notifications.show(t('drawer.user.promoApplied'), { severity: 'success', autoHideDuration: 3000 });
        setHasPromo(true);
      }
      setPromoDialogOpen(false);
    } catch (e) {
      notifications.show(e?.message || 'Action failed', { severity: 'error' });
    } finally {
      setPromoActionLoading(false);
    }
  }, [user?.id, hasPromo, chosenTariffId, notifications, t]);

  const handleAddComment = useCallback(async (text) => {
    if (!user?.id) return;
    const res = await api.commentUser(user.id, text);
    const list = res?.data?.comments;
    if (Array.isArray(list)) setComments(list);
    else setComments((prev) => [...prev, { at: new Date().toISOString(), by: null, text }]);
    notifications.show(t('drawer.comments.added'), { severity: 'success', autoHideDuration: 2000 });
  }, [user?.id, notifications, t]);

  const handleOpenCardsDialog = useCallback(() => {
    if (!user?.id) return;
    setCardsDialogOpen(true);
    setCardsError(null);
    setCardsLoading(true);
    api
      .GetUserCardApi(user.id)
      .then((res) => {
        const data = res?.data;
        setCards(Array.isArray(data) ? data : data?.data ?? []);
      })
      .catch((e) => setCardsError(e?.message || t('drawer.user.failedToLoadCards')))
      .finally(() => setCardsLoading(false));
  }, [user?.id]);

  const handleBlockClick = useCallback(() => setBlockDialogOpen(true), []);
  const handleBlockConfirm = useCallback(async () => {
    if (!user?.phoneNumber) return;
    setBlockActionLoading(true);
    try {
      await api.lockUser(user.phoneNumber, !blocking);
      setBlocking(!blocking);
      setBlockDialogOpen(false);
      onRefresh?.();
      notifications.show(blocking ? t('drawer.user.userRestored') : t('drawer.user.userBlocked'), { severity: 'success', autoHideDuration: 3000 });
    } catch (e) {
      notifications.show(e?.message || t('drawer.user.actionFailed'), { severity: 'error' });
    } finally {
      setBlockActionLoading(false);
    }
  }, [user?.phoneNumber, blocking, onRefresh, notifications, t]);

  const handleDeleteClick = useCallback(() => setDeleteDialogOpen(true), []);
  const handleDeleteConfirm = useCallback(async () => {
    if (!user?.phoneNumber) return;
    setDeleteActionLoading(true);
    try {
      await api.DeleteUsersApi(user.phoneNumber);
      setDeleteDialogOpen(false);
      onClose();
      onRefresh?.();
      notifications.show(t('drawer.user.userDeleted'), { severity: 'success', autoHideDuration: 3000 });
    } catch (e) {
      notifications.show(e?.message || t('drawer.user.deleteFailed'), { severity: 'error' });
    } finally {
      setDeleteActionLoading(false);
    }
  }, [user?.phoneNumber, onClose, onRefresh, notifications, t]);

  const handleCopy = useCallback(
    (text) => {
      if (!text || text === '—') return;
      navigator.clipboard.writeText(String(text)).then(
        () => notifications.show(t('drawer.copied'), { severity: 'success', autoHideDuration: 2000 }),
        () => notifications.show(t('drawer.copyFailed'), { severity: 'error' }),
      );
    },
    [notifications, t],
  );

  const handleOpenRentInRents = useCallback(
    (rentId) => {
      onClose();
      try {
        sessionStorage.setItem(CROSS_LINK_RENT_ID, String(rentId));
      } catch (_) {}
      history.push('/rents');
    },
    [onClose, history],
  );

  return (
    <>
      <DetailDrawer title={t('drawer.user.title')} open={open} onClose={onClose}>
        <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
          {!user ? (
            <Typography color="text.secondary">{t('drawer.user.noSelection')}</Typography>
          ) : (
            <>
          <Tabs
            value={detailTab}
            onChange={(_, v) => setDetailTab(v)}
            variant="fullWidth"
            sx={{ minHeight: 40, mb: 1 }}
          >
            <Tab label={t('drawer.user.tabInfo')} id="user-detail-tab-0" aria-controls="user-detail-panel-0" />
            <Tab
              label={rents.length > 0 ? t('drawer.user.tabRentHistoryCount', { count: rents.length }) : t('drawer.user.tabRentHistory')}
              id="user-detail-tab-1"
              aria-controls="user-detail-panel-1"
            />
          </Tabs>

          <Box
            id="user-detail-panel-0"
            role="tabpanel"
            hidden={detailTab !== 0}
            aria-labelledby="user-detail-tab-0"
            sx={{ flex: 1, overflow: 'auto', minHeight: 0 }}
          >
            <Stack spacing={2} sx={{ pb: 2 }}>
            <Section title={t('drawer.user.mainInfo')}>
              <CopyableField label={t('drawer.user.id')} value={user.id} onCopy={handleCopy} copyTooltip={t('drawer.user.copy')} />
              <Field label={t('drawer.user.registrationDate')} value={formatDate(user.createdDate)} />
              <Field label={t('drawer.user.email')} value={user.email || '—'} />
              <CopyableField label={t('drawer.user.phone')} value={user.phoneNumber} onCopy={handleCopy} copyTooltip={t('drawer.user.copy')} />
              <Box sx={{ py: 0.75 }}>
                <Typography variant="caption" color="text.secondary" display="block">{t('drawer.user.status')}</Typography>
                <Chip
                  label={blocking ? t('drawer.user.blocked') : t('drawer.user.active')}
                  color={blocking ? 'default' : 'success'}
                  size="small"
                />
              </Box>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 0.5, py: 0.75 }}>
                <Typography variant="caption" color="text.secondary" sx={{ flex: '0 0 auto' }}>{t('drawer.user.cards')}</Typography>
                <Tooltip title={t('drawer.user.viewCards')}>
                  <IconButton
                    aria-label={t('drawer.user.viewCards')}
                    onClick={handleOpenCardsDialog}
                    size="small"
                    color="primary"
                  >
                    <CreditCardIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
            </Section>

            <Section title={t('drawer.user.givePromo')}>
              <Button
                variant={hasPromo ? 'outlined' : 'contained'}
                color="primary"
                startIcon={<CardGiftcardOutlinedIcon />}
                onClick={handleOpenPromoDialog}
                fullWidth
              >
                {hasPromo ? t('drawer.user.stopPromo') : t('drawer.user.givePromo')}
              </Button>
            </Section>

            <Section title={t('drawer.user.actions')}>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                <Button
                  variant={blocking ? 'contained' : 'outlined'}
                  color={blocking ? 'primary' : 'secondary'}
                  startIcon={blocking ? <LockOpenIcon /> : <BlockIcon />}
                  onClick={handleBlockClick}
                >
                  {blocking ? t('drawer.user.restoreUser') : t('drawer.user.blockUser')}
                </Button>
                <Button
                  variant="outlined"
                  color="error"
                  startIcon={<DeleteOutlineIcon />}
                  onClick={handleDeleteClick}
                >
                  {t('drawer.user.deleteUser')}
                </Button>
              </Stack>
            </Section>

            <EntityCommentsSection
              comments={comments}
              onAdd={handleAddComment}
            />
            </Stack>
          </Box>

          <Box
            id="user-detail-panel-1"
            role="tabpanel"
            hidden={detailTab !== 1}
            aria-labelledby="user-detail-tab-1"
            sx={{ flex: 1, overflow: 'auto', minHeight: 0 }}
          >
            <Stack spacing={2} sx={{ pb: 2 }}>
            {rentsLoading && (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                <CircularProgress size={24} />
              </Box>
            )}
            {rentsError && <Alert severity="error" sx={{ py: 0 }}>{rentsError}</Alert>}
            {!rentsLoading && !rentsError && rents.length > 0 && (
              <Paper variant="outlined" sx={{ p: 1.5, bgcolor: 'action.hover' }}>
                <Stack spacing={1.5}>
                  {rents.map((rent, idx) => {
                    const currency = getRentCurrencySymbol(rent);
                    const totalCost = rent.totalAmount != null ? `${rent.totalAmount} ${currency}` : '—';
                    const hasDebt = rent.debt != null && Number(rent.debt) !== 0;
                    const debtStr = rent.debt != null ? `${rent.debt} ${currency}` : null;
                    return (
                      <Box key={rent.id || idx} sx={{ py: 0.75, borderBottom: idx < rents.length - 1 ? 1 : 0, borderColor: 'divider' }}>
                        <Stack direction="row" alignItems="flex-start" justifyContent="space-between" gap={0.5} flexWrap="wrap">
                          <Box sx={{ minWidth: 0, flex: 1 }}>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>{rent.id || t('drawer.user.rentId', { n: idx + 1 })}</Typography>
                            {rent.startDate && (
                              <Typography variant="caption" color="text.secondary" display="block">
                                {formatDate(rent.startDate)}
                                {rent.finishedDate ? ` – ${formatDate(rent.finishedDate)}` : ''}
                              </Typography>
                            )}
                            <Typography variant="caption" color="text.secondary">
                              {t('drawer.user.totalCost')}: {totalCost}
                            </Typography>
                            {hasDebt && debtStr != null && (
                              <Typography variant="caption" color="warning.main" display="block" sx={{ mt: 0.25 }}>
                                {t('drawer.user.debt')}: {debtStr}
                              </Typography>
                            )}
                          </Box>
                          <Tooltip title={t('drawer.user.openInRent')}>
                            <IconButton size="small" aria-label={t('drawer.user.openInRent')} onClick={() => handleOpenRentInRents(rent.id)}>
                              <OpenInNewOutlinedIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      </Box>
                    );
                  })}
                </Stack>
              </Paper>
            )}
            {!rentsLoading && !rentsError && rents.length === 0 && (
              <Typography variant="body2" color="text.secondary">{t('drawer.user.noRents')}</Typography>
            )}
            </Stack>
          </Box>
            </>
          )}
        </Box>
      </DetailDrawer>

      <Dialog open={promoDialogOpen} onClose={() => setPromoDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>
          {hasPromo ? t('drawer.user.promoDialogTitleStop') : t('drawer.user.promoDialogTitleGive')}
        </DialogTitle>
        <DialogContent>
          {hasPromo ? (
            <Typography>{t('drawer.user.promoConfirmStop')}</Typography>
          ) : (
            <FormControl fullWidth size="small" sx={{ mt: 1 }}>
              <InputLabel>{t('drawer.user.promoPlan')}</InputLabel>
              <Select
                value={chosenTariffId}
                label={t('drawer.user.promoPlan')}
                onChange={(e) => {
                  setChosenTariffId(e.target.value);
                }}
              >
                {promoTariffs.map((t) => (
                  <MenuItem key={t.id} value={t.id}>{t.groupName}</MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPromoDialogOpen(false)}>{t('drawer.user.cancel')}</Button>
          <Button variant="contained" onClick={handleGivePromo} disabled={promoActionLoading}>
            {promoActionLoading ? '…' : hasPromo ? t('drawer.user.stop') : t('drawer.user.apply')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={cardsDialogOpen} onClose={() => setCardsDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{t('drawer.user.cardsDialogTitle')}</DialogTitle>
        <DialogContent>
          {cardsLoading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
              <CircularProgress />
            </Box>
          )}
          {cardsError && (
            <Alert severity="error" sx={{ mt: 1 }}>{cardsError}</Alert>
          )}
          {!cardsLoading && !cardsError && (
            cards.length === 0 ? (
              <Typography color="text.secondary">{t('drawer.user.noCards')}</Typography>
            ) : (
              <List dense>
                {cards.map((card, idx) => {
                  const first6 = card.first6 || '';
                  const last4 = card.last4 || '';
                  const mask = first6.length >= 6 && last4.length >= 4
                    ? `${first6.slice(0, 4)} ${first6.slice(4, 6)}** **** ${last4.slice(0, 4)}`
                    : card.mask || '—';
                  const expiryYear = (card.expiry_year || '').toString();
                  const expiryStr = card.expiry_month != null && expiryYear
                    ? `${String(card.expiry_month).padStart(2, '0')}/${expiryYear.length >= 4 ? expiryYear.slice(-2) : expiryYear}`
                    : '—';
                  const primary = card.card_type || (card.source === 'apple_pay' ? 'Apple Pay' : card.source === 'gpay' ? 'Google Pay' : 'Card');
                  const secondary = `${mask} · Expires ${expiryStr}`;
                  return (
                    <ListItem key={card.id || idx} divider={idx < cards.length - 1}>
                      <ListItemText primary={primary} secondary={secondary} />
                    </ListItem>
                  );
                })}
              </List>
            )
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCardsDialogOpen(false)}>{t('drawer.user.close')}</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={blockDialogOpen} onClose={() => !blockActionLoading && setBlockDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>{blocking ? t('drawer.user.blockDialogTitleRestore') : t('drawer.user.blockDialogTitleBlock')}</DialogTitle>
        <DialogContent>
          <Typography>
            {blocking
              ? t('drawer.user.blockConfirmRestore', { id: user?.phoneNumber ?? user?.id ?? '' })
              : t('drawer.user.blockConfirmBlock', { id: user?.phoneNumber ?? user?.id ?? '' })}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBlockDialogOpen(false)} disabled={blockActionLoading}>{t('drawer.user.cancel')}</Button>
          <Button variant="contained" onClick={handleBlockConfirm} disabled={blockActionLoading}>
            {blockActionLoading ? '…' : blocking ? t('drawer.user.restore') : t('drawer.user.block')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={deleteDialogOpen} onClose={() => !deleteActionLoading && setDeleteDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>{t('drawer.user.deleteDialogTitle')}</DialogTitle>
        <DialogContent>
          <Typography>
            {t('drawer.user.deleteConfirm', { id: user?.phoneNumber ?? user?.id ?? '' })}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} disabled={deleteActionLoading}>{t('drawer.user.cancel')}</Button>
          <Button variant="contained" color="error" onClick={handleDeleteConfirm} disabled={deleteActionLoading}>
            {deleteActionLoading ? '…' : t('drawer.user.delete')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
